import { useEffect } from 'react';
import { useStableCallback } from './use-stable-callback';

interface UseKeyDownOptions {
   /** Whether the keydown listener is enabled. Defaults to true. */
   enabled?: boolean;
}

/**
 * Hook to listen for keydown events on the document.
 *
 * @param callback Function to call when a key is pressed. Receives the keyboard event.
 * @param options Configuration options for the hook.
 *
 * @example
 * ```tsx
 * // Basic usage - listens to all keydown events
 * useKeyDown(event => {
 *   if (event.key === 'Escape') closeModal();
 * });
 *
 * // Conditional usage - only when modal is open
 * useKeyDown(event => {
 *   if (event.key === 'Escape') closeModal();
 * }, { enabled: isModalOpen });
 * ```
 */
export function useKeyDown(
   callback: (event: KeyboardEvent) => void,
   options: UseKeyDownOptions = {}
) {
   const { enabled = true } = options;
   const stableCallback = useStableCallback(callback);

   useEffect(() => {
      if (!enabled) return;

      const onKeyDown = (e: KeyboardEvent) => {
         stableCallback(e);
      };

      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
   }, [enabled, stableCallback]);
}
